/* ══════════════════════════════════════════════════════════════════════════
   A LECKE LEÍRÁSA MARKDOWNKÉNT (Tananyag 1. kör) — tiszta elemző, React nélkül
   ─────────────────────────────────────────────────────────────────────────
   Melyik leírás markdown? Amelyikben van `## ` címsor vagy `![…](…)` kép. A régi,
   sima szöveges leírások (✓-os sorok, linkek) ilyet nem tartalmaznak (élő ellenőrzés:
   0 találat), ezek a régi LessonDescription-nel jelennek meg, változatlanul.

   Csak a leckék elemeit ismeri: `##` címsor, `- ` felsorolás, `1. ` számozott lista,
   `> ` idézet, kép (a következő sorban dőlt képaláírással), bekezdés; soron belül
   **félkövér**, *dőlt* és [link](cím). Minden más szövegként jelenik meg: nyers HTML
   sem renderelődik (a megjelenítő React-elemeket ad, innerHTML nélkül).

   Képek: a bucket címe, a méret a # utáni részben (a kép így az admin MDEditorában is
   látszik és szerkeszthető):
     ![alt](https://…/lesson-images/kripto-start/kor1/k08-06-1.webp#w=1600&h=900&mw=1080&mh=1350)
   A mobilváltozat fájlnév-konvencióval: ugyanott <név>_mobil.webp; az mw/mh jelzi, hogy van.
   ══════════════════════════════════════════════════════════════════════════ */

export const isMarkdown = (text) => typeof text === 'string' && (/^##\s/m.test(text) || /!\[[^\]]*\]\([^)]+\)/.test(text));

/** A kép címe → { src, w, h, mobil: { src, w, h } | null }; csak http(s) cím (más: null). */
export function kepForras(url) {
  const [cim, frag = ''] = String(url || '').split('#');
  if (!/^https:\/\/[^\s"'<>]+$/.test(cim)) return null;
  const p = new URLSearchParams(frag);
  const num = (k) => { const n = Number(p.get(k)); return Number.isInteger(n) && n > 0 ? n : null; };
  const w = num('w'), h = num('h'), mw = num('mw'), mh = num('mh');
  return {
    src: cim, w, h,
    mobil: mw && mh ? { src: cim.replace(/(\.[a-z0-9]+)$/i, '_mobil$1'), w: mw, h: mh } : null,
  };
}

/** A link célja: belső útvonal (`/…`) az alkalmazáson belül, https külső; más nem link. */
export function linkCel(url) {
  const u = String(url || '').trim();
  if (/^\/(?!\/)[A-Za-z0-9/_\-?=&#.%]*$/.test(u)) return { belso: true, to: u };
  if (/^https:\/\/[^\s"'<>]+$/.test(u)) return { belso: false, to: u };
  return null;
}

/* ── Soron belüli elemek ── */

/** „a **b** [c](/d) *e*” → [{t:'text'}, {t:'b', c:[…]}, {t:'link', to, belso, c:[…]}, {t:'i', c:[…]}] */
export function parseInline(s) {
  const out = [];
  let rest = String(s || '');
  const push = (text) => { if (!text) return; const last = out[out.length - 1]; if (last?.t === 'text') last.v += text; else out.push({ t: 'text', v: text }); };
  while (rest) {
    const m = rest.match(/\*\*(.+?)\*\*|\*([^*\s][^*]*?)\*|\[([^\]]+)\]\(([^)\s]+)\)/);
    if (!m) { push(rest); break; }
    push(rest.slice(0, m.index));
    if (m[1] !== undefined) out.push({ t: 'b', c: parseInline(m[1]) });
    else if (m[2] !== undefined) out.push({ t: 'i', c: parseInline(m[2]) });
    else {
      const cel = linkCel(m[4]);
      if (cel) out.push({ t: 'link', ...cel, c: parseInline(m[3]) });
      else push(m[0]);
    }
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

/* ── Blokkok ── */

const KEP = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/;
const ALAIRAS = /^\*([^*].*[^*]|[^*])\*\s*$/;
const BLOKK_ELEJE = /^(##\s|-\s|\d+\.\s|>\s?|!\[)/;

/** A leírás → blokkok: h2 | ul | ol | quote | figure | p. */
export function parseMarkdown(text) {
  // tűrés: ha egy kép a sor közepén kezdődik és a sor végéig tart (pl. a képaláírás után elmaradt a
  // sortörés), külön sorba kerül, így kép marad, és nem nyers jelölésként jelenik meg
  const lines = String(text || '').replace(/\r\n?/g, '\n')
    .replace(/(\S)[ \t]*(!\[[^\]\n]*\]\([^)\s]+\)[ \t]*)$/gm, '$1\n$2').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }
    let m;
    if ((m = line.match(/^##\s+(.*)$/))) { blocks.push({ t: 'h2', c: parseInline(m[1].trim()) }); i += 1; continue; }
    if ((m = line.match(KEP))) {
      const kep = kepForras(m[2]);
      const alairas = lines[i + 1]?.match(ALAIRAS);
      if (kep) blocks.push({ t: 'figure', alt: m[1], kep, alairas: alairas ? parseInline(alairas[1]) : null });
      else blocks.push({ t: 'p', c: [{ t: 'text', v: m[1] }] });
      i += alairas ? 2 : 1;
      continue;
    }
    if (/^>\s?/.test(line)) {
      const sor = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { sor.push(lines[i].replace(/^>\s?/, '')); i += 1; }
      blocks.push({ t: 'quote', c: parseInline(sor.join(' ').trim()) });
      continue;
    }
    const lista = line.match(/^-\s+/) ? { t: 'ul', re: /^-\s+/ } : line.match(/^\d+\.\s+/) ? { t: 'ol', re: /^\d+\.\s+/ } : null;
    if (lista) {
      const items = [];
      while (i < lines.length && lines[i].trim()) {
        if (lista.re.test(lines[i])) items.push(lines[i].replace(lista.re, ''));
        else if (BLOKK_ELEJE.test(lines[i])) break;
        else items[items.length - 1] += ` ${lines[i].trim()}`;
        i += 1;
      }
      blocks.push({ t: lista.t, items: items.map(parseInline) });
      continue;
    }
    const sor = [];
    while (i < lines.length && lines[i].trim() && (sor.length === 0 || !BLOKK_ELEJE.test(lines[i]))) { sor.push(lines[i].trim()); i += 1; }
    blocks.push({ t: 'p', c: parseInline(sor.join(' ')) });
  }
  return blocks;
}
